"use client";

import Link from "next/link";
import React from "react";
import EntypoLink from "react-entypo/lib/entypo/Link";
import Gist from "react-gist";
import { TwitterTweetEmbed } from "react-twitter-embed";
import ReactYoutube from "react-youtube";
import { cx as classNames } from "class-variance-authority";

import { generateColorFromIndex } from "../../utils/colors";
import { BrowserOnly } from "../BrowserOnly";
import { OptimizedImage } from "../OptimizedImage";
import { ReactTinyLink } from "./ReactTinyLink";

export function YouTube(props: { videoId: string } & any) {
  return (
    <div className="flex justify-center mb-4 blog-post-text">
      <ReactYoutube opts={{ width: "370" }} {...props} />
    </div>
  );
}

export function Dot() {
  return (
    <div className="flex justify-center w-full my-6 space-x-8 text-sm font-thin text-gray-700">
      <div>•</div>
    </div>
  );
}

export function DotDotDot() {
  return (
    <div className="flex justify-center w-full my-6 space-x-8 text-sm font-thin text-gray-700">
      <div>•</div>
      <div>•</div>
      <div>•</div>
    </div>
  );
}

export function Image({
  src,
  alt,
  caption = alt,
  width,
  height,
  maxWidth = 1200,
  align = "center",
}: {
  src: string;
  srcSmall?: string;
  alt: string;
  caption: React.ReactNode;
  width: string;
  height: string;
  maxWidth?: number;
  align: "left" | "center" | "right";
}) {
  const widthNum = Number.parseInt(width);
  const heightNum = Number.parseInt(height);
  const aspectRatio = widthNum / heightNum;

  const actualWidth = maxWidth ? Math.min(maxWidth, widthNum) : widthNum;
  const actualHeight = maxWidth ? actualWidth / aspectRatio : height;

  const theImage = (
    <OptimizedImage
      src={src}
      alt={alt}
      width={actualWidth}
      height={actualHeight}
      className="rounded-t-md"
    />
  );

  return (
    <div className="flex justify-center blog-full-bleed">
      <div
        style={{ maxWidth: `${width}px` }}
        className="inline-flex flex-col items-center justify-center mx-auto my-8 rounded-md card-shadow"
      >
        <div className="">{theImage}</div>
        <div className="p-3 text-base text-center text-gray-600">{caption}</div>
      </div>
    </div>
  );
}

export function HeaderWithAnchor({ id, number, children }) {
  const color = generateColorFromIndex(number);

  return (
    <>
      <div className="relative blog-post-text">
        <div id={id} style={{ top: "-4rem" }} className="absolute invisible">
          .
        </div>
        <h2
          style={{ borderBottom: `${color} 2px solid` }}
          className={classNames(
            "text-3xl mt-16 mb-2 font-bold text-center -ml-3 md:text-left md:ml-1",
            "__css_header-with-anchor"
          )}
        >
          <span
            style={{ color: color }}
            className="mr-2 underline md:opacity-0"
          >
            <a
              href={`#${id}`}
              title={children}
              suppressHydrationWarning={true}
              className=""
            >
              <EntypoLink
                style={{
                  verticalAlign: "text-bottom",
                  marginBottom: "0.24rem",
                }}
              />
            </a>
          </span>
          {children}
        </h2>
      </div>
    </>
  );
}

export function Note({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="blog-post-text">
      <div className="px-6 py-4 mx-8 my-12 text-sm bg-gray-100 border-l-4 border-r-4 border-purple-500 rounded card-shadow">
        <div className="mb-2 font-semibold tracking-wider text-gray-700 uppercase">
          {title}
        </div>
        <div className="text-base text-gray-700">{children}</div>
      </div>
    </div>
  );
}

export function Callout({ children }) {
  return (
    <div className="my-16 text-3xl font-semibold text-gray-600 blog-post-text">
      <div className="mx-10">{children}</div>
    </div>
  );
}

export function FootnoteLink({ number }) {
  return (
    <Link href={`#footnote-${number}`} passHref legacyBehavior>
      <a className="text-blue-600 hover:font-bold">
        <sup>[{number}]</sup>
      </a>
    </Link>
  );
}

export function Footnote({ number, children }) {
  return (
    <>
      <div className="relative mb-4 blog-post-text">
        <div
          id={`footnote-${number}`}
          style={{ top: "-5rem" }}
          className="absolute invisible"
        >
          .
        </div>
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "40px 1fr",
            }}
            className="container"
          >
            <div className="-mt-2 text-3xl font-bold">{number}.</div>
            <div className="">{children}</div>
          </div>
        </div>
      </div>
    </>
  );
}
export function EmbedLink({ url }) {
  return (
    <div className="mb-4 break-words blog-post-text">
      <BrowserOnly>
        <ReactTinyLink
          cardSize="small"
          showGraphic={true}
          maxLine={2}
          minLine={1}
          url={url}
        />
      </BrowserOnly>
    </div>
  );
}

export function BigQuotes({ children }) {
  return (
    <span className="flex items-center py-4 space-x-2">
      <div
        style={{ transform: "translateY(5px)" }}
        className="text-6xl text-gray-500"
      >
        “
      </div>
      <div>{children}</div>
      <div
        style={{ transform: "translateY(25px)" }}
        className="text-6xl text-gray-500"
      >
        ”
      </div>
    </span>
  );
}

export function Tweet({ tweetId, isMultiple }) {
  return (
    <>
      <style jsx global>{`
        .twitter-tweet {
          max-width: 100% !important;
        }
        .embedded-tweet {
          min-height: ${isMultiple ? 420 : 300}px;
        }
        .embedded-tweet > div {
          width: 550px;
        }
      `}</style>
      <div className="flex justify-center w-full py-2 embedded-tweet">
        <TwitterTweetEmbed tweetId={tweetId} options={{ width: "400" }} />
      </div>
    </>
  );
}

export function EmbedGithubGist(props: { id: string } & any) {
  return (
    <div>
      <Gist {...props} />
    </div>
  );
}

export function SimpleExternalLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="italic font-semibold text-blue-600 underline"
    >
      {children}
    </a>
  );
}
